/**
 * React Query hooks for Workflow Schedules
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { workflowSchedulesApi } from '@/lib/api';
import type {
  ScheduleCreateRequest,
  ScheduleUpdateRequest,
  ScheduleListParams,
} from '@/types/workflowSchedule';

// Query keys
export const scheduleKeys = {
  all: ['workflow-schedules'] as const,
  lists: () => [...scheduleKeys.all, 'list'] as const,
  list: (params?: ScheduleListParams) => [...scheduleKeys.lists(), params] as const,
  details: () => [...scheduleKeys.all, 'detail'] as const,
  detail: (id: string) => [...scheduleKeys.details(), id] as const,
  executions: (id: string) => [...scheduleKeys.all, 'executions', id] as const,
};

/**
 * Hook for listing schedules
 */
export function useScheduleList(params?: ScheduleListParams) {
  return useQuery({
    queryKey: scheduleKeys.list(params),
    queryFn: () => workflowSchedulesApi.list(params),
  });
}

/**
 * Hook for getting a single schedule
 */
export function useSchedule(scheduleId: string) {
  return useQuery({
    queryKey: scheduleKeys.detail(scheduleId),
    queryFn: () => workflowSchedulesApi.get(scheduleId),
    enabled: !!scheduleId,
  });
}

/**
 * Hook for creating a schedule
 */
export function useCreateSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ScheduleCreateRequest) => workflowSchedulesApi.create(data),
    onSuccess: (schedule) => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.lists() });
      toast.success(`计划 "${schedule.name}" 已创建`);
    },
    onError: (error: Error) => {
      toast.error(`创建失败: ${error.message}`);
    },
  });
}

/**
 * Hook for updating a schedule
 */
export function useUpdateSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ScheduleUpdateRequest }) =>
      workflowSchedulesApi.update(id, data),
    onSuccess: (schedule) => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: scheduleKeys.detail(schedule.id) });
      toast.success(`计划 "${schedule.name}" 已更新`);
    },
    onError: (error: Error) => {
      toast.error(`更新失败: ${error.message}`);
    },
  });
}

/**
 * Hook for deleting a schedule
 */
export function useDeleteSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (scheduleId: string) => workflowSchedulesApi.delete(scheduleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.lists() });
      toast.success('计划已删除');
    },
    onError: (error: Error) => {
      toast.error(`删除失败: ${error.message}`);
    },
  });
}

/**
 * Hook for toggling schedule enabled state
 */
export function useToggleSchedule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled?: boolean }) =>
      workflowSchedulesApi.toggle(id, enabled),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: scheduleKeys.detail(response.id) });
      toast.success(response.is_enabled ? '已启用' : '已禁用');
    },
    onError: (error: Error) => {
      toast.error(`操作失败: ${error.message}`);
    },
  });
}

/**
 * Hook for manually triggering a schedule
 */
export function useRunScheduleNow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (scheduleId: string) => workflowSchedulesApi.runNow(scheduleId),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: scheduleKeys.executions(response.schedule_id) });
      toast.success('执行已触发');
    },
    onError: (error: Error) => {
      toast.error(`执行失败: ${error.message}`);
    },
  });
}

/**
 * Hook for getting execution history
 */
export function useScheduleExecutions(scheduleId: string, params?: { limit?: number; offset?: number }) {
  return useQuery({
    queryKey: [...scheduleKeys.executions(scheduleId), params],
    queryFn: () => workflowSchedulesApi.getExecutions(scheduleId, params),
    enabled: !!scheduleId,
  });
}
